/**
 * What a running client plan freezes, and what it does not.
 *
 * Ported from `move-with-me-frontend/.../plans/builder/model/livePlan.ts` — which is one predicate
 * — and extended with the cycle-level gates the canvas needs to render a locked cycle before the
 * trainer touches it.
 *
 * **These mirror a server rule; they do not enforce one.** R4 invariant 2 freezes the phase
 * structure of the consumed prefix, R7 makes logged history immutable, and R3 regenerates only
 * not-started upcoming workouts. The backend re-checks all of it on every save. The point of
 * mirroring is the view ADR §8 rule:
 *
 * > No control is enabled whose action the server will refuse.
 *
 * Mobile's C5 defect — a phase-length stepper that stayed enabled on a running plan while the
 * server rejected every resulting save — is what that rule is made of.
 *
 * ── The two live coordinates are NOT interchangeable, and confusing them is the known trap ────
 * `lastConsumedCycle` is the gate. `firstOpenCycle` is a subtitle. Gating on `firstOpenCycle`
 * locked a detached fork and unlocked an exhausted one, both wrong, because it is null for a
 * paused or exhausted plan too. `firstOpenCycleIsNeverAnEditGate` in the tests pins that.
 */

import type { PlanLiveContext } from './planDraft.types';

/**
 * Is this a running client fork rather than a library template?
 *
 * Drives the chrome — a running plan updates in place rather than being re-assigned, and its
 * confirmations name the client. It is deliberately NOT the lock: a fork whose assignment has been
 * detached or has never been started locks nothing, and `lastConsumedCycle` is what says so.
 */
export function isLivePlan(live: PlanLiveContext): boolean {
  return live.isForkTemplate;
}

/**
 * How many leading cycles the client has already consumed — started, finished, or let pass
 * unstarted. Zero when there is no live assignment behind this template, and zero when there is one
 * the client has not begun.
 *
 * The two zeroes mean different things to the SERVER (no assignment versus an untouched one), but
 * they mean the same thing to the canvas: nothing is frozen. `isLivePlan` is what tells them apart
 * where the difference matters.
 */
export function consumedCycleCount(live: PlanLiveContext): number {
  return live.lastConsumedCycle ?? 0;
}

/**
 * Is this absolute cycle (1-based) locked?
 *
 * `cycle <= lastConsumedCycle`, and nothing else. `lastConsumedCycle === 0` locks nothing — the
 * backend's own `PhaseGrowthPolicy` says so in as many words: "the client has trained nothing —
 * leaves the whole structure free".
 */
export function isCycleLocked(live: PlanLiveContext, cycle: number): boolean {
  return cycle <= consumedCycleCount(live);
}

/**
 * Does this phase reach into the consumed prefix?
 *
 * A phase that merely STRADDLES the boundary is already partly frozen, and the consumed-prefix
 * guard refuses a structural edit to it just as it would to a fully consumed one. So the question
 * is answered by the phase's FIRST cycle alone — if that one is consumed the phase is inside the
 * prefix, and if it is not, no later cycle of the same phase can be either. The phase's length
 * is deliberately not a parameter: taking one would imply it changes the answer.
 */
export function isPhaseLocked(live: PlanLiveContext, startCycle: number): boolean {
  return isCycleLocked(live, Math.max(1, startCycle));
}

/**
 * What the top of the builder states in words about the plan being live.
 *
 * `null` on a plan with no live assignment behind it at all — a library template, or a fork whose
 * assignment was detached. Those two lock nothing and belong to nobody, so the chip is absent
 * rather than reading something about a client who is not there.
 *
 * ── An ASSIGNED plan says so even before the client has trained ───────────────────────────────
 * It used to be `null` for `consumedCycles === 0` too, on the reasoning that "0 cycles done" is a
 * sentence about nothing. The premise is right and the conclusion was wrong: what the chip says is
 * not only how much is frozen, it is that a real person's calendar is downstream of this canvas.
 *
 * That plan is the one where saying so matters most, and it is the case a self-paced plan makes
 * routine. Nothing is consumed until the client walks to it, so a plan assigned weeks ago can sit
 * at zero indefinitely — and the trainer restructured it with no more warning than they get on an
 * untouched library template, then saved, and the whole tree went out over the client's live
 * assignment. Every occurrence the save regenerates is one the client's app has already shown them.
 *
 * `consumedCycles: 0` is therefore a legal value of this object and the caller words it
 * differently: the assignment stated, with no cycle range, because there is no range to state.
 */
export interface LivePlanLock {
  /**
   * Cycles 1..`consumedCycles` are frozen. **Zero on a plan the client has not begun**, which locks
   * nothing and is still a plan somebody is on.
   */
  readonly consumedCycles: number;
  /** Total cycles the plan spans, when known — the "of N" half of the subtitle. */
  readonly totalCycles: number | null;
}

export function livePlanLock(live: PlanLiveContext): LivePlanLock | null {
  // `lastConsumedCycle != null` is the same coordinate `isPlanModeLocked` reads, and for the same
  // reason: it is the field that separates "there is an assignment" from "there was one" — a
  // detached fork reports null and is free again, and `consumedCycleCount`'s `?? 0` folds that
  // together with an untouched assignment, which is exactly the distinction this needs.
  if (!isLivePlan(live) || live.lastConsumedCycle == null) return null;
  return { consumedCycles: consumedCycleCount(live), totalCycles: live.totalCycles };
}

/**
 * Is the plan-level SCHEDULING MODE frozen?
 *
 * ── It has no callers, and that is a statement about the product, not an oversight ────────────
 * The builder's system control stopped gating on this on 16 August 2026: `self-paced-plan-requirements.md`
 * R1 fixes a plan's mode at CREATION, so the control states the mode rather than offering a flip and
 * there is no longer a toggle for a lock to close. The predicate is kept because it still answers a
 * true question about a fork — the backend's `plan_mode_locked` refusal is real and a future surface
 * that offers any mode-shaped edit will need exactly this — and because the reasoning below is the
 * record of why the answer is not `isPhaseLocked`. Delete it with the owner, not in passing.
 *
 * The flip it was written for was weekly ⇄ sequence. `SequenceBased` has since been purged from the
 * wire vocabulary entirely, so the pair it named no longer exists; the rule it expresses does.
 *
 * Assigning a plan materialises its occurrences against one mode, so the backend refuses the flip
 * afterwards with `plan_mode_locked`. Mobile's `isPlanModeLocked` (`phaseStepperLimits.ts`) is one
 * line over `isLiveLocked`, and this is that line: a fork the backend reports a live assignment
 * for, whatever the client has or has not trained yet.
 *
 * ── It is NOT `isPhaseLocked`, and the difference is the whole point ─────────────────────────
 * `isPhaseLocked` asks "has the client consumed this far", so it opens up again at
 * `lastConsumedCycle === 0`. The MODE has nothing to do with what has been trained: the occurrences
 * exist the moment the plan is assigned, and cycle 0 of a brand-new assignment already has a
 * calendar built against one mode. Gating the toggle on `consumedCycleCount(live) > 0` would offer
 * a flip that the server answers with a 400 on the very assignments most likely to be edited —
 * exactly the C5 defect the header names.
 *
 * A fork whose assignment was DETACHED reports `lastConsumedCycle: null` and is free again, which
 * is what `null` already means everywhere else in this module.
 */
export function isPlanModeLocked(live: PlanLiveContext): boolean {
  return isLivePlan(live) && live.lastConsumedCycle != null;
}
